// hooks/usePageMeta.js — each public page's own title and description.
//
// The site is one HTML file, so every URL used to share index.html's title,
// description and canonical link: /pricing told Google it was a copy of the
// home page, and every tab read the same words. This sets them per page, and
// puts back whatever was there when the page goes away, so a signed-in screen
// with no entry here never inherits "Pricing".
//
// The words come from copy/product.js (BRAND, SITE_TITLE, SITE_DESCRIPTION),
// so the name and the positioning line change in one place.
import { useEffect } from 'react';
import { BRAND, SITE_URL, SITE_TITLE, SITE_DESCRIPTION } from '../copy/product';

const titled = (page) => `${page} | ${BRAND}`;

export const PAGE_META = {
  landing: { path: '/', title: SITE_TITLE, description: SITE_DESCRIPTION },
  pricing: {
    path: '/pricing',
    title: titled('Pricing'),
    description: 'Start free with 15 applications a month applied for you. Pro is $18 a month and Max $48 when billing opens.',
  },
  extension: {
    path: '/extension',
    title: titled('The AI extension'),
    description: `The ${BRAND} extension reads each job application, fills it in from your resume and saved answers, and writes what is left for you to check.`,
  },
  privacy: {
    path: '/privacy',
    title: titled('Privacy Policy'),
    description: `What ${BRAND} keeps about you, why, who it goes to, and how to delete it.`,
  },
  terms: {
    path: '/terms-of-service',
    title: titled('Terms of Service'),
    description: `The terms for using ${BRAND} and its browser extension.`,
  },
  security: {
    path: '/security',
    title: titled('Security'),
    description: `How ${BRAND} protects your account and your data, and how to report a security problem.`,
  },
  login: {
    path: '/login',
    title: titled('Log in'),
    description: `Log in to ${BRAND} and pick up where you left off.`,
  },
  register: {
    path: '/register',
    title: titled('Create your account'),
    description: `Create a free ${BRAND} account. No card needed.`,
  },
  // Not a real page: no canonical of its own, and kept out of search.
  notFound: { title: titled('Page not found'), description: SITE_DESCRIPTION, noindex: true },
};

const head = () => document.head;
const metaTag = (attr, key) => head().querySelector(`meta[${attr}="${key}"]`);

// The tags this hook owns, read and written as one record.
function read() {
  return {
    title: document.title,
    description: metaTag('name', 'description')?.getAttribute('content') ?? null,
    ogTitle: metaTag('property', 'og:title')?.getAttribute('content') ?? null,
    ogDescription: metaTag('property', 'og:description')?.getAttribute('content') ?? null,
    ogUrl: metaTag('property', 'og:url')?.getAttribute('content') ?? null,
    twTitle: metaTag('name', 'twitter:title')?.getAttribute('content') ?? null,
    twDescription: metaTag('name', 'twitter:description')?.getAttribute('content') ?? null,
    canonical: head().querySelector('link[rel="canonical"]')?.getAttribute('href') ?? null,
    robots: metaTag('name', 'robots')?.getAttribute('content') ?? null,
  };
}

function setMeta(attr, key, value) {
  let tag = metaTag(attr, key);
  if (value == null) { tag?.remove(); return; }
  if (!tag) {
    tag = document.createElement('meta');
    tag.setAttribute(attr, key);
    head().appendChild(tag);
  }
  tag.setAttribute('content', value);
}

function write(r) {
  document.title = r.title;
  setMeta('name', 'description', r.description);
  setMeta('property', 'og:title', r.ogTitle);
  setMeta('property', 'og:description', r.ogDescription);
  setMeta('property', 'og:url', r.ogUrl);
  setMeta('name', 'twitter:title', r.twTitle);
  setMeta('name', 'twitter:description', r.twDescription);
  setMeta('name', 'robots', r.robots);
  let link = head().querySelector('link[rel="canonical"]');
  if (r.canonical == null) { link?.remove(); return; }
  if (!link) {
    link = document.createElement('link');
    link.setAttribute('rel', 'canonical');
    head().appendChild(link);
  }
  link.setAttribute('href', r.canonical);
}

export default function usePageMeta(key) {
  useEffect(() => {
    const meta = PAGE_META[key];
    if (!meta || typeof document === 'undefined') return undefined;
    const before = read();
    const url = meta.path ? `${SITE_URL}${meta.path}` : before.canonical;
    write({
      title: meta.title,
      description: meta.description,
      ogTitle: meta.title,
      ogDescription: meta.description,
      ogUrl: meta.noindex ? before.ogUrl : url,
      twTitle: meta.title,
      twDescription: meta.description,
      canonical: meta.noindex ? null : url,
      robots: meta.noindex ? 'noindex' : before.robots,
    });
    return () => write(before);
  }, [key]);
}
